import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { Moment } from 'moment';
import type { Appointment } from 'services/types/appointment';
import { NAVY, STATUS_GOOD, STATUS_WARN, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import AppointmentCard from './AppointmentCard';
import { appointmentsOnDay } from './serviceCalendar.utils';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      color: 'rgba(21, 24, 58, 0.45)',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

const Stat = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.05em',
        color: 'rgba(21, 24, 58, 0.45)',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color }}>{value}</Typography>
  </Box>
);

interface Props {
  day: Moment;
  appointments: Appointment[];
  onSelectAppointment: (appointment: Appointment) => void;
}

const DaySummary = ({ day, appointments, onSelectAppointment }: Props) => {
  const dayAppointments = appointmentsOnDay(appointments, day);
  const done = dayAppointments.filter((a) => a.status === 'done').length;
  const pending = dayAppointments.filter((a) => a.status === 'pending').length;

  return (
    <Box
      sx={{
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        p: 2.5,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        borderRadius: 3,
        boxShadow: '0px 1px 3px rgba(21, 24, 58, 0.08)',
        overflow: 'auto',
      }}
    >
      <Box>
        <SectionLabel>Resumen del día</SectionLabel>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
          <Stat label="Total" value={dayAppointments.length} color={NAVY} />
          <Stat label="Realizadas" value={done} color={STATUS_GOOD} />
          <Stat label="Pendientes" value={pending} color={STATUS_WARN} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <SectionLabel>Próximas citas</SectionLabel>
        {dayAppointments.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: 'rgba(21, 24, 58, 0.5)' }}>No hay citas para este día.</Typography>
        ) : (
          dayAppointments.map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              variant="list"
              onClick={onSelectAppointment}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default DaySummary;
